import { isNil } from 'lodash-es';
import { DB_TYPE } from '@/common/define/common.define';
import {
  getGetV7RedshiftSqlTextByInstanceIdQueryKey,
  getV7RedshiftSqlTextByInstanceId,
} from '@/api-client/redshift/queries/redshift-controller/redshift-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import { getRedshiftSqlDetailTitle } from '@/database/components/sqlDetail/common/utils';
import type {
  DocumentTextParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type RedshiftSessionDetailAddition,
  type SqlDetailSchema,
  type RedshiftSqlDetailAddition,
  type ParameterDetailSchema,
  type RedshiftParameterDetailAddition,
  type DocumentTextSchema,
} from './redshift.schema';

export const getRedshiftSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
}: SessionDetailParams): { name: string; addition: RedshiftSessionDetailAddition } => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.pid),
    addition: {
      dbType: DB_TYPE.REDSHIFT,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      pid: data.pid,
      query: data.query,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
    },
  };
};

export const getRedshiftSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: RedshiftSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: getRedshiftSqlDetailTitle(data),
    addition: {
      dbType: DB_TYPE.REDSHIFT,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      sqlId: data.sqlId,
      xmSqlId: data.xmSqlId,
      query: data.query,
    },
  };
};

export const getRedshiftParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: RedshiftParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.REDSHIFT,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getRedshiftDocumentTextParams = ({
  detailInfo,
  instanceId,
  useSampleText,
  useFieldValidation,
}: DocumentTextParams) => {
  if (!useFieldValidation) {
    return isNil(useSampleText)
      ? { instanceId, normalizedSqlId: detailInfo.sqlId, xmSqlId: detailInfo.xmSqlId }
      : {
          instanceId,
          normalizedSqlId: detailInfo.sqlId,
          xmSqlId: useSampleText ? undefined : detailInfo.xmSqlId,
          sampling: useSampleText ? true : undefined,
        };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return isNil(useSampleText)
    ? { instanceId: data.instanceId, normalizedSqlId: data.sqlId, xmSqlId: data.xmSqlId }
    : {
        instanceId: data.instanceId,
        normalizedSqlId: data.sqlId,
        xmSqlId: useSampleText ? undefined : data.xmSqlId,
        sampling: useSampleText ? true : undefined,
      };
};

export const fetchRedshiftDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getRedshiftDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7RedshiftSqlTextByInstanceId(
    params.instanceId!,
    { normalizedSqlId: params.normalizedSqlId, xmSqlId: params.xmSqlId, sampling: params.sampling },
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0]?.sqlText || null;
};

export const getRedshiftDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7RedshiftSqlTextByInstanceIdQueryKey();
  }

  const convertedParams = getRedshiftDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7RedshiftSqlTextByInstanceIdQueryKey(convertedParams.instanceId, convertedParams);
};

export const redshiftSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const redshiftSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const redshiftDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
